import type { ListingFormImage, ListingRowForEdit } from './types';
import { isBlobUrl } from '@/lib/listingImages';

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function collectKeys(row: ListingRowForEdit): string[] {
  const fromArray = Array.isArray(row.images)
    ? row.images.filter((v): v is string => typeof v === 'string' && v.trim() !== '' && !isBlobUrl(v))
    : [];
  if (fromArray.length > 0) return fromArray;

  const cover = row.cover_image_url;
  if (typeof cover === 'string' && cover.trim() && !isBlobUrl(cover)) return [cover.trim()];

  const legacy = row.image_url;
  if (typeof legacy === 'string' && legacy.trim() && !isBlobUrl(legacy)) return [legacy.trim()];

  return [];
}

export function imagesFromListingRow(row: ListingRowForEdit): ListingFormImage[] {
  const keys = collectKeys(row);
  const cover = typeof row.cover_image_url === 'string' ? row.cover_image_url.trim() : null;
  let coverIndex = cover ? keys.findIndex((k) => k === cover) : 0;
  if (coverIndex < 0) coverIndex = 0;

  return keys.map((key, index) => ({
    id: newId(),
    storageKey: key,
    previewUrl: null,
    file: null,
    isCover: keys.length > 0 ? index === coverIndex : false,
  }));
}

export function createLocalImages(files: File[]): ListingFormImage[] {
  return files.map((file) => ({
    id: newId(),
    storageKey: null,
    previewUrl: URL.createObjectURL(file),
    file,
    isCover: false,
  }));
}

export function revokePreviewUrls(images: ListingFormImage[]): void {
  for (const img of images) {
    if (img.previewUrl && img.previewUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(img.previewUrl);
      } catch {
        // ignore
      }
    }
  }
}

export function moveImage(
  images: ListingFormImage[],
  index: number,
  direction: -1 | 1
): ListingFormImage[] {
  const next = index + direction;
  if (index < 0 || next < 0 || index >= images.length || next >= images.length) {
    return images;
  }
  const copy = [...images];
  const tmp = copy[index];
  copy[index] = copy[next];
  copy[next] = tmp;
  return copy;
}

export function setCoverAt(images: ListingFormImage[], index: number): ListingFormImage[] {
  if (index < 0 || index >= images.length) return images;
  return images.map((img, i) => ({ ...img, isCover: i === index }));
}

export function removeImageAt(images: ListingFormImage[], index: number): ListingFormImage[] {
  if (index < 0 || index >= images.length) return images;
  const removed = images[index];
  const remaining = images.filter((_, i) => i !== index);
  if (removed.previewUrl?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(removed.previewUrl);
    } catch {
      // ignore
    }
  }
  if (remaining.length === 0) return [];
  if (removed.isCover || !remaining.some((img) => img.isCover)) {
    return remaining.map((img, i) => ({ ...img, isCover: i === 0 }));
  }
  return remaining;
}

export function ensureCover(images: ListingFormImage[]): ListingFormImage[] {
  if (images.length === 0) return images;
  if (images.some((img) => img.isCover)) return images;
  return images.map((img, i) => ({ ...img, isCover: i === 0 }));
}

export function getCoverIndex(images: ListingFormImage[]): number | null {
  if (images.length === 0) return null;
  const idx = images.findIndex((img) => img.isCover);
  return idx >= 0 ? idx : 0;
}

export const MAX_LISTING_IMAGES = 20;
